import ScrollTextReveal from "@/components/anim/ScrollTextReveal";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  Star,
  Users,
  Sparkles,
  Target,
  Heart,
  Compass,
  Brain,
  Play,
  Zap,
  Shield,
} from "lucide-react";
import { FileText as SampleReportIcon } from "lucide-react";
import WeKitLogo from "@/components/WeKitLogo";
import GoogleSignUpButton from "@/components/GoogleSignUpButton";
import PageHead from "@/components/PageHead";
import VideoHero from "@/components/VideoHero";
import ProductTourEmbed from "@/components/home/ProductTourEmbed";
import AskAiSection from "@/components/home/AskAiSection";
import ScrollReveal from "@/components/ScrollReveal";
import MagneticButton from "@/components/anim/MagneticButton";
import SharedFooter from "@/components/SharedFooter";
import TrustRibbon from "@/components/TrustRibbon";
import Testimonials from "@/components/Testimonials";
import GlobalLaunchWaitlist from "@/components/GlobalLaunchWaitlist";
import WCC360LeadCta from "@/components/WCC360LeadCta";
import { GlobalPill, OFFER } from "@/components/OfferCrumbs";
import LaunchOfferCountdown from "@/components/LaunchOfferCountdown";
import FomoOfferBar from "@/components/FomoOfferBar";
import FreebiePopup from "@/components/FreebiePopup";
import { OFFER_FOMO_LINES } from "@/config/pricing";
import OfferHeadlineLink from "@/components/OfferHeadlineLink";


import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import dashboardShot from "@/assets/screenshots/dashboard.jpg.asset.json";
import journeyShot from "@/assets/screenshots/journey.jpg.asset.json";
import visionBoardShot from "@/assets/screenshots/vision-board.jpg.asset.json";
import careersShot from "@/assets/screenshots/careers.jpg.asset.json";

const HERO_VIDEO = "https://videos.pexels.com/video-files/3129671/3129671-uhd_2560_1440_30fps.mp4";
const SECTION_VIDEO = "https://videos.pexels.com/video-files/3195394/3195394-uhd_2560_1440_25fps.mp4";

const stats = [
  { value: "5,877", label: "careers mapped", icon: Compass },
  { value: "Personal", label: "questions, made for you", icon: Brain },
  { value: "30–60 min", label: "at your own pace", icon: Zap },
  { value: "8", label: "archetypes you'll place into", icon: Target },
];

const journeyLoop = [
  {
    when: "Day 1",
    title: "Take the assessment",
    desc: "personal questions, 30–60 minutes, mapped against 5,000+ careers of the future.",
    href: "/payment",
  },
  {
    when: "Week 4",
    title: "First Reflection",
    desc: "Monthly check-in — what shifted, what surprised you?",
    href: "/reflect/1",
  },
  {
    when: "Month 3",
    title: "Skill check-in",
    desc: "Track progress on your roadmap. Career\u00a0 nudges kick in. YoMa Progress nudges kick in.",
    href: "/journey",
  },
  {
    when: "Month 12",
    title: "Career Wrapped",
    desc: "Your year, visualised. Share it. Reset for the next chapter.",
    href: "/wrapped",
  },
];

const features = [
  {
    icon: Compass,
    title: "Holistic 360° Insights",
    description: "Go beyond personality tests with a deeper understanding of strengths, values, motivations, EQ and leadership potential.",
    image: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    icon: Brain,
    title: "5,000+ careers of the future, mapped",
    description: "Classified against international occupation standards, from emerging tech to timeless trades.",
    image: "https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    icon: Heart,
    title: "Dignity of Labor",
    description: "We celebrate both white-collar and blue-collar careers — because every vocation matters.",
    image: "https://images.pexels.com/photos/4145153/pexels-photo-4145153.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    icon: Play,
    title: "\"Day in the Life\" Career Exploration",
    description: "Understand what professionals actually do — not just job titles. Who will you spend your day with, how does a day in the life roll out.",
    image: "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    icon: Shield,
    title: "Pros AND Cons of Every Role",
    description: "See the realities, challenges and rewards before making important decisions. Understand what the perks and drudge work for each career role is.",
    image: "https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    icon: Sparkles,
    title: "Personalized Growth Roadmaps",
    description: "Receive actionable recommendations and practical next steps — not generic reports.",
    image: "https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
];

const steps = [
  {
    num: "01",
    title: "Discover Yourself",
    desc: "Personal, specific questions. 30–60 minutes. Written in plain language.",
  },
  {
    num: "02",
    title: "Explore Your Career DNA",
    desc: "Gain deep insights into your strengths, interests, motivations and learning style.",
  },
  {
    num: "03",
    title: "Build Your Career Roadmap",
    desc: "Explore careers, skills and actionable next steps with confidence. Build your Vision Board and your Future Command Center.\u00a0\n\n\nGet actionable next steps for the next 30 days.",
  },
];

const Index = () => {
  const parallaxRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll();
  const navBg = useTransform(scrollYProgress, [0, 0.05], [0, 1]);

  return (
    <div className="relative isolate min-h-screen bg-background text-foreground">
      <PageHead
        title="WeKIT™ Career Clarity 360 | Find Your Ideal Career"
        description="Discover who you're becoming. personal questions, 30–60 minutes, mapped against 5,000+ careers of the future. Your future should be a choice, not a guess."
        path="/"
      />
      <ScrollTextReveal />
      <FreebiePopup />
      {/* Sitewide FOMO bar + Floating Nav */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <FomoOfferBar />
      <motion.nav
        className="glass-nav"
        style={{ opacity: 1 }}
      >
        <div className="container mx-auto flex h-16 items-center justify-between gap-2 px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2 min-w-0">
            <WeKitLogo size="md" />
            <span className="font-display text-base sm:text-lg font-bold text-foreground truncate">WeKIT™</span>
          </Link>
          <div className="hidden items-center gap-8 md:flex">
            <a href="#how-it-works" className="text-sm font-medium text-muted-foreground transition-colors duration-300 hover:text-foreground">How It Works</a>
            <a href="#features" className="text-sm font-medium text-muted-foreground transition-colors duration-300 hover:text-foreground">Features</a>
            <a href="#pricing" className="text-sm font-medium text-muted-foreground transition-colors duration-300 hover:text-foreground">Pricing</a>
            <a href="https://www.wekitmentoring.com/" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-muted-foreground transition-colors duration-300 hover:text-foreground">Mentoring</a>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <GoogleSignUpButton className="sm:hidden" />

            <Button size="sm" variant="ghost" className="rounded-full hidden sm:inline-flex" asChild>
              <Link to="/auth?mode=signin">Sign in</Link>
            </Button>
            <MagneticButton as="div" strength={0.35}>
              <Button size="sm" className="rounded-full px-3 sm:px-6 hover-glow" asChild>
                <Link to="/auth?mode=signup" aria-label="Start Now">
                  <span className="hidden xs:inline sm:inline">Start Now</span>
                  <span className="xs:hidden sm:hidden">Start</span>
                  <ArrowRight className="ml-1 sm:ml-1.5 h-4 w-4" />
                </Link>
              </Button>
            </MagneticButton>
          </div>
        </div>
      </motion.nav>
      </div>

      {/* HERO — Cinematic Video */}
      <VideoHero videoUrl={HERO_VIDEO}>
        <div className="container mx-auto px-6 py-20 md:py-28">
          <div className="mx-auto max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <GlobalPill />
            </motion.div>




            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.6 }}
              className="mt-6 text-[11px] font-semibold uppercase tracking-[0.28em] text-primary/90"
            >
              WeKIT™ <span className="text-foreground/60">= Wot Kareer Is it?</span>
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
              className="mt-4 font-display text-5xl font-bold leading-[1.05] tracking-tight text-foreground md:text-7xl lg:text-8xl"
            >
              Discover the career
              <br />
              you're <span className="gradient-text">wired for</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.32, duration: 0.6 }}
              className="mt-6 font-display text-lg italic text-primary/90 md:text-xl"
            >
              Rooting <span className="not-italic font-bold">YOU</span> in <span className="not-italic font-bold">YOUr</span> Purpose.
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="mt-3 text-[10.5px] font-semibold uppercase tracking-[0.24em] text-muted-foreground md:text-xs"
            >
              Powered by the <span className="text-foreground">WeKIT™ Human Development Intelligence Engine</span>
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.48, duration: 0.6 }}
              className="mt-8 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg"
            >
              Stop guessing. Start building a future with clarity.&nbsp;
              <br />WeKIT™ Career Clarity 360&nbsp;
              <br />combines psychometric science, AI and real-world career intelligence to help
              <br />you - students, professionals, anyone uncover their strengths and explore 5,000+ careers of the future.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center"
            >
              <MagneticButton as="div" strength={0.3}>
                <Button size="lg" className="h-14 w-full rounded-full px-8 text-base font-semibold hover-glow sm:w-auto" asChild>
                  <Link to="/payment">
                    Start My Career Clarity Journey <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </MagneticButton>
              <MagneticButton as="div" strength={0.25}>
                <Button
                  variant="outline"
                  size="lg"
                  className="h-14 w-full rounded-full border-foreground/20 px-8 text-base font-semibold backdrop-blur-sm hover:bg-foreground/5 sm:w-auto"
                  asChild
                >
                  <a href="#how-it-works">
                    <Play className="mr-2 h-4 w-4" /> See How It Works
                  </a>
                </Button>
              </MagneticButton>
              <MagneticButton as="div" strength={0.25}>
                <Button
                  variant="ghost"
                  size="lg"
                  className="h-14 w-full rounded-full px-8 text-base font-semibold hover:bg-foreground/5 sm:w-auto"
                  asChild
                >
                  <Link to="/demo">
                    <SampleReportIcon className="mr-2 h-4 w-4" /> Watch Sample Report
                  </Link>
                </Button>
              </MagneticButton>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="mt-6 flex max-w-xl items-start gap-3 border-l-2 border-primary pl-4 text-sm leading-relaxed text-muted-foreground md:text-base"
            >
              Created by mentors, educators and AI product leaders committed to helping every young person discover purpose.
            </motion.p>
          </div>
        </div>
      </VideoHero>

      {/* TRUST SIGNALS — directly below the hero */}
      <TrustRibbon />

      {/* STATS — Glass cards */}
      <section className="relative z-20 pt-16 pb-16">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {stats.map((stat, i) => (
              <ScrollReveal key={stat.label} delay={i * 100}>
                <div className="glass-card p-6 text-center hover-lift">
                  <stat.icon className="mx-auto mb-3 h-5 w-5 text-primary" />
                  <div className="font-display text-2xl md:text-3xl font-bold text-foreground leading-tight">{stat.value}</div>
                  <div className="mt-1 text-xs sm:text-sm text-muted-foreground">{stat.label}</div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEM / VISION */}
      <section className="py-32 section-band-surface">
        <div className="container mx-auto px-6">
          <div className="mx-auto max-w-4xl text-center">
            <ScrollReveal>
              <p className="text-sm font-medium uppercase tracking-widest text-primary">The Problem</p>
              <h2 className="mt-6 font-display text-4xl font-bold leading-tight text-foreground md:text-6xl text-balance">
                Most students are choosing careers <span className="gradient-text">blindly</span>.
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <p className="mt-8 text-xl leading-relaxed text-muted-foreground text-balance">
                Parents worry. Students feel overwhelmed. Schools lack personalized guidance.
                Traditional career advice focuses on marks — not purpose, strengths, and the
                future of work.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={400}>
              <div className="mt-12 inline-flex items-center gap-3 rounded-full border border-primary/20 bg-primary/5 px-8 py-4">
                <Sparkles className="h-5 w-5 text-primary" />
                <span className="text-lg font-medium text-foreground">
                  WeKIT™ helps young people move from confusion to clarity — personal questions, 30–60 minutes.
                </span>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ABOUT WeKIT™ — Brand intro */}
      <section id="about" className="py-20 sm:py-24 md:py-32 section-band-green">
        <div className="container mx-auto px-5 sm:px-6">
          <div className="grid gap-12 md:gap-14 lg:grid-cols-2 lg:gap-20 items-start">
            <ScrollReveal>
              <div className="max-w-xl">
                <p className="text-xs sm:text-sm font-medium uppercase tracking-[0.2em] text-primary">
                  Rooting Youth in Their Purpose
                </p>
                <h2 className="mt-4 font-display text-[2rem] leading-[1.15] sm:text-4xl md:text-5xl font-bold text-foreground text-balance break-words">
                  More than <span className="gradient-text">career guidance</span>.
                </h2>
                <p className="mt-5 sm:mt-6 text-base sm:text-lg leading-relaxed text-muted-foreground">
                  We help young people discover who they are — and where they belong.
                </p>
                <p className="mt-4 text-sm sm:text-base leading-relaxed text-muted-foreground">
                  Through psychometric insights and AI-powered career intelligence, WeKIT™
                  helps students align strengths, interests, and values with meaningful work.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={150}>
              <div className="glass-card p-6 sm:p-8 md:p-10">
                <h3 className="font-display text-xl sm:text-2xl font-semibold text-foreground">
                  What We Offer
                </h3>
                <ul className="mt-5 sm:mt-6 space-y-3 sm:space-y-4">
                  {[
                    { icon: Brain, text: "AI-powered career intelligence" },
                    { icon: Compass, text: "WeKIT™ Career Clarity 360 psychometric assessment" },
                    { icon: Zap, text: "5,000+ careers of the future, mapped" },
                    { icon: Sparkles, text: "Future-ready recommendations" },
                    { icon: Target, text: "Personalized roadmaps" },
                    { icon: Users, text: "Support for students, parents and mentors" },
                  ].map((item) => (
                    <li key={item.text} className="flex items-start gap-3 sm:gap-4">
                      <div className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                        <item.icon className="h-4 w-4 sm:h-[18px] sm:w-[18px] text-primary" />
                      </div>
                      <span className="min-w-0 flex-1 pt-1 sm:pt-1.5 text-sm sm:text-base leading-relaxed text-foreground">
                        {item.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <AskAiSection />

      {/* HOW IT WORKS — Steps */}
      <section id="how-it-works" className="py-32 section-band-surface">
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <div className="text-center">
              <p className="text-sm font-medium uppercase tracking-widest text-primary">How It Works</p>
              <h2 className="mt-4 font-display text-4xl font-bold text-foreground md:text-5xl whitespace-pre-line">
                WeKIT™{"\u00a0"}Career Clarity 360{"\u00a0"}{"\n\n"}in three simple steps
              </h2>
            </div>
          </ScrollReveal>

          <div className="mt-20 grid gap-8 md:grid-cols-3">
            {steps.map((step, i) => (
              <ScrollReveal key={step.num} delay={i * 150}>
                <div className="glass-card p-8 hover-lift h-full">
                  <span className="font-display text-6xl font-bold text-primary/15">{step.num}</span>
                  <h3 className="mt-4 font-display text-xl font-semibold text-foreground">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{step.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT TOUR — interactive walkthrough, right after the three steps */}
      <section id="see-it" className="py-24 sm:py-28 section-band-zaffre">
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-medium uppercase tracking-widest text-primary">See it in action</p>
              <h2 className="mt-4 font-display text-4xl font-bold text-foreground md:text-5xl text-balance">
                Watch it work before you begin
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                A short guided tour of the experience your child will walk through.
              </p>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={120}>
            <div className="mx-auto mt-12 max-w-5xl">
              <ProductTourEmbed />
            </div>
          </ScrollReveal>
        </div>
      </section>



      {/* RETENTION LOOP — What happens after the roadmap */}
      <section className="py-24 section-band-surface">
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-sm font-medium uppercase tracking-widest text-primary">After the roadmap</p>
              <h2 className="mt-4 font-display text-3xl md:text-5xl font-bold text-foreground text-balance">
                Your journey doesn't end at the report.
              </h2>
              <p className="mt-4 text-muted-foreground text-lg">
                Career development isn't a one-time decision. WeKIT™ grows with you for the full 12 months — and beyond.
              </p>
            </div>
          </ScrollReveal>

          <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {journeyLoop.map((item, i) => (
              <ScrollReveal key={item.when} delay={i * 100}>
                <Link
                  to={item.href}
                  className="group block h-full glass-card p-6 hover-lift relative overflow-hidden"
                >
                  <span className="text-[10px] font-bold uppercase tracking-widest text-primary">{item.when}</span>
                  <h3 className="mt-3 font-display text-lg font-semibold text-foreground">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.desc}</p>
                  <ArrowRight className="mt-4 h-4 w-4 text-primary transition-transform group-hover:translate-x-1" />
                </Link>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={500}>
            <div className="mt-10 mx-auto max-w-2xl rounded-2xl border border-primary/20 bg-primary/5 p-5 flex items-start gap-4">
              <Users className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-foreground">Always on: mentors & community (Coming in Q4 2026)</p>
                <p className="text-muted-foreground mt-1">
                  Match with mentors, join cohorts, and unlock referrals from day one — not just at milestones.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>



      {/* FEATURES — Cinematic cards with images */}
      <section id="features" className="py-32 section-band-green">
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <div className="text-center">
              <p className="text-sm font-medium uppercase tracking-widest text-primary">Why WeKIT™</p>
              <h2 className="mt-4 font-display text-4xl font-bold text-foreground md:text-5xl">
                Why WeKIT™ Career Clarity 360?
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
                Six reasons students, parents and schools choose WeKIT™ over a generic personality test.
              </p>
            </div>
          </ScrollReveal>

          <div className="mt-20 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, i) => (
              <ScrollReveal key={feature.title} delay={i * 100}>
                <div className="group relative overflow-hidden rounded-2xl border border-border bg-card hover-lift h-full">
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/20 backdrop-blur-sm">
                        <feature.icon className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                  </div>
                  {/* Content */}
                  <div className="p-6">
                    <h3 className="font-display text-lg font-semibold text-foreground">{feature.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* VISUAL STORYTELLING — Full width video section */}
      <section className="cinematic-section py-0">
        <div className="relative min-h-[80vh] overflow-hidden">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 h-full w-full object-cover"
          >
            <source src={SECTION_VIDEO} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
          <div className="relative z-10 flex min-h-[80vh] items-center">
            <div className="container mx-auto px-6">
              <div className="max-w-xl">
                <ScrollReveal>
                  <p className="text-sm font-medium uppercase tracking-widest text-primary">Sample Result</p>
                  <h2 className="mt-6 font-display text-4xl font-bold leading-tight text-foreground md:text-5xl">
                    Imagine knowing where you <span className="gradient-text">fit best</span>.
                  </h2>
                  <p className="mt-6 text-lg text-muted-foreground">
                    Discover careers that match your strengths — not someone else's expectations.
                  </p>
                  <div className="mt-6 rounded-2xl border border-primary/20 bg-background/40 p-5 backdrop-blur-sm">
                    <p className="font-display text-2xl font-semibold text-foreground">
                      Law — <span className="text-primary">92% Match</span>
                    </p>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Communication + empathy = your superpower.
                    </p>
                    <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                      {[
                        "A day in the life",
                        "Salary potential",
                        "Required skills",
                        "Growth opportunities",
                        "Challenges and trade-offs",
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-2 text-sm text-foreground">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <MagneticButton as="div" strength={0.3} className="mt-10">
                    <Button size="lg" className="h-14 rounded-full px-10 text-base font-semibold hover-glow" asChild>
                      <Link to="/payment">
                        Discover Your Career DNA <ArrowRight className="ml-2 h-5 w-5" />
                      </Link>
                    </Button>
                  </MagneticButton>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PEEK INSIDE — App screenshots */}
      <section id="peek-inside" className="py-24 sm:py-32 section-band-zaffre">
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-medium uppercase tracking-widest text-primary">A peek inside</p>
              <h2 className="mt-4 font-display text-4xl font-bold text-foreground md:text-5xl">
                See what you'll actually use.
              </h2>
              <p className="mt-4 text-muted-foreground">
                Four screens from the live WCC360 experience — your dashboard, your 12-month journey, your future command center, and 5,000+ careers of the future to explore.
              </p>
            </div>
          </ScrollReveal>

          <div className="mx-auto mt-14 grid max-w-6xl gap-6 md:grid-cols-2">
            {[
              {
                src: dashboardShot.url,
                eyebrow: "Your Dashboard",
                title: "One home for your career journey",
                desc: "XP, streaks, next steps, and every artefact — reports, vision board, sharing — one click away.",
                href: "/dashboard",
              },
              {
                src: journeyShot.url,
                eyebrow: "My Growth Journey",
                title: "12 months, mapped month by month",
                desc: "5 assessments across the year, monthly reflections, milestones, and badges as you grow.",
                href: "/journey",
              },
              {
                src: visionBoardShot.url,
                eyebrow: "Future Command Center",
                title: "Turn dreams into an AI-powered roadmap",
                desc: "Who you are. Who you want to become. Life Wheel. All grounded in your Career Clarity 360 results.",
                href: "/vision-board",
              },
              {
                src: careersShot.url,
                eyebrow: "Careers Explorer",
                title: "5,877 future-ready careers",
                desc: "A day in the life, salary bands, demand outlook, and the pros & cons no one else tells you.",
                href: "/careers",
              },
            ].map((s, i) => (
              <ScrollReveal key={s.eyebrow} delay={i * 80}>
                <figure className="group relative overflow-hidden rounded-3xl border border-border/60 bg-card/60 shadow-lg shadow-primary/[0.04] backdrop-blur transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10">
                  <div className="relative aspect-[16/10] overflow-hidden bg-black">
                    <img
                      src={s.src}
                      alt={`${s.eyebrow} — ${s.title}`}
                      loading="lazy"
                      className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background/70 to-transparent" />
                  </div>
                  <figcaption className="p-6 sm:p-7">
                    <p className="text-xs font-semibold uppercase tracking-widest text-primary">{s.eyebrow}</p>
                    <h3 className="mt-2 font-display text-xl font-semibold text-foreground sm:text-2xl">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
                  </figcaption>
                </figure>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={200}>
            <div className="mt-12 flex justify-center">
              <Button asChild size="lg" className="rounded-full h-12 px-6 text-base font-semibold hover-glow">
                <Link to="/payment">
                  Start your 12-month journey <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-32 section-band-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <div className="text-center">
              <p className="text-sm font-medium uppercase tracking-widest text-primary">Pricing</p>
              <h2 className="mt-4 font-display text-4xl font-bold text-foreground md:text-5xl">
                Your 12-month Career Growth Journey.
              </h2>
              <p className="mt-4 text-muted-foreground">
                One price. Twelve months of AI-powered career guidance with 5 assessments, continuous progress tracking, and a Career Intelligence Dashboard that evolves with you.
              </p>
              {OFFER_FOMO_LINES.length > 0 && (
                <>
                  <div className="mx-auto mt-6 flex max-w-2xl justify-center">
                    <OfferHeadlineLink
                      surface="pricing"
                      className="text-center text-base font-bold leading-snug text-primary"
                    />
                  </div>
                  <div className="mt-3 flex justify-center">
                  </div>
                </>
              )}

            </div>
          </ScrollReveal>

          <div className="mx-auto mt-16 grid max-w-4xl gap-8 md:grid-cols-2 md:items-stretch">
            <ScrollReveal delay={0} className="h-full">
              <div className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-primary/30 bg-card p-8 hover-lift">
                <div className="absolute top-0 right-0 h-32 w-32 rounded-bl-full bg-primary/5" />

                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="font-display text-5xl font-bold text-foreground">{OFFER.now}</span>
                  {OFFER.isFlash && <span className="text-lg text-muted-foreground line-through">{OFFER.was}</span>}
                  <span className="text-sm text-muted-foreground">{OFFER.unit}</span>
                </div>
                {OFFER.isFlash && (
                  <>
                    <Badge variant="secondary" className="mt-3 self-start rounded-full">
                      {OFFER.save} • {OFFER.endsLabel}
                    </Badge>
                    <div className="mt-4">
                      <LaunchOfferCountdown />
                    </div>
                  </>
                )}
                <p className="mt-4 text-sm text-muted-foreground">
                  Launch pricing while we roll out. Once you're in, you never start from zero again.
                </p>
                <p className="mt-6 text-sm text-muted-foreground">
                  12 months of AI-powered career guidance — 5 assessments, continuous progress tracking, personalised reports, and dashboard access.
                </p>
                <ul className="mt-4 space-y-4">
                  {[
                    "AI-Powered Career Clarity 360 Assessment",
                    "Purpose & Career Fit Profile",
                    "Strengths & Future Skills Analysis",
                    "Career Match & Learning Pathways",
                    "Personal Career Intelligence Report (PDF)",
                    "AI Career Coach & Personalised Insights",
                    "5 Career Check-ins across 12 months (sign-up, 3, 6, 9, 12 mo)",
                    "Continuous growth tracking on your Career Intelligence Dashboard",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-foreground">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 text-primary shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <MagneticButton as="div" strength={0.3} className="mt-auto w-full">
                  <Button className="pt-0 w-full h-14 rounded-full text-base font-semibold hover-glow" asChild>
                    <Link to="/payment" className="!mt-8">Discover My Future <ArrowRight className="ml-2 h-4 w-4" /></Link>
                  </Button>
                </MagneticButton>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={150} className="h-full">
              <div className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card p-8 hover-lift">
                <div className="mb-6 flex items-center gap-3">
                  <Heart className="h-5 w-5 text-primary" />
                  <span className="font-display text-lg font-semibold text-foreground">Scholarship Path</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-5xl font-bold text-foreground">25–100%</span>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">
                  Because no student's future should be limited by finances.
                </p>
                <ul className="mt-6 space-y-4">
                  {[
                    "Need-based financial assistance",
                    "Includes all WeKIT™ Career Clarity 360 benefits",
                    "Simple application process",
                    "Response within 48 hours",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-foreground">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 text-primary shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button variant="outline" className="mt-auto w-full h-14 rounded-full text-base font-semibold border-foreground/20 hover:bg-foreground/5" asChild>
                  <a href="https://www.napoleonrobertsfoundation.org/career-clarity" target="_blank" rel="noopener noreferrer">
                    Apply for Scholarship Support <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
                <p className="mt-4 text-center text-xs text-muted-foreground">
                  🏆 Powered by the Napoleon Roberts Foundation
                </p>
              </div>
            </ScrollReveal>
          </div>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Not ready yet?{" "}
            <Link to="/free" className="font-medium text-primary underline underline-offset-4">
              Start free — future ready starts here
            </Link>
          </p>
        </div>
      </section>

      {/* SECURE SIGN IN — compact */}
      <section id="secure-sign-in" className="py-12 sm:py-16 section-band-surface">
        <div className="container mx-auto px-5 sm:px-6">
          <div className="mx-auto max-w-3xl">
            <ScrollReveal>
              <div className="glass-card p-5 sm:p-6">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
                  <div className="min-w-0 flex-1">
                    <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-2.5 py-0.5">
                      <Shield className="h-3 w-3 text-primary" />
                      <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-primary">
                        Secure Sign In
                      </span>
                    </div>
                    <h2 className="mt-3 font-display text-xl sm:text-2xl font-bold leading-snug text-foreground text-balance">
                      Start your journey toward <span className="gradient-text">clarity</span>
                    </h2>
                    <p className="mt-2 text-xs sm:text-sm text-muted-foreground">
                      Join thousands of students discovering careers aligned with their strengths and aspirations.
                    </p>
                    <ul className="mt-3 grid gap-1.5 sm:grid-cols-2 sm:gap-x-4">
                      {[
                        "WeKIT™ Career Clarity 360 assessments",
                        "Personalized reports",
                        "Progress saved across WeKIT™",
                        "Mentor & learning matches",
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                          <span className="min-w-0 flex-1">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button
                    size="sm"
                    className="h-11 shrink-0 rounded-full px-5 text-sm font-semibold hover-glow"
                    asChild
                  >
                    <Link to="/auth">
                      Sign in with Google <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Link>
                  </Button>
                </div>

                <Accordion type="single" collapsible className="mt-4 border-t border-border/60 pt-2">
                  <AccordionItem value="why" className="border-border/50">
                    <AccordionTrigger className="py-2.5 text-left text-xs sm:text-sm font-medium">
                      Why Google sign-in?
                    </AccordionTrigger>
                    <AccordionContent className="text-xs sm:text-sm leading-relaxed text-muted-foreground">
                      Faster, safer access — no extra password, protected by Google's security (including 2-step verification if enabled).
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="data" className="border-border/50">
                    <AccordionTrigger className="py-2.5 text-left text-xs sm:text-sm font-medium">
                      What data is shared?
                    </AccordionTrigger>
                    <AccordionContent className="text-xs sm:text-sm leading-relaxed text-muted-foreground">
                      Only basic profile: name, email, picture. We never receive your password, contacts, or calendar — and never post.
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="manage" className="border-b-0">
                    <AccordionTrigger className="py-2.5 text-left text-xs sm:text-sm font-medium">
                      Can I revoke access?
                    </AccordionTrigger>
                    <AccordionContent className="text-xs sm:text-sm leading-relaxed text-muted-foreground">
                      Yes — manage anytime at{" "}
                      <a
                        href="https://myaccount.google.com/permissions"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-foreground"
                      >
                        myaccount.google.com/permissions
                      </a>
                      .
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                <p className="mt-3 text-[11px] sm:text-xs text-muted-foreground">
                  🔒 Basic Google profile only. See our{" "}
                  <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link> · manage in{" "}
                  <a
                    href="https://myaccount.google.com/permissions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-foreground"
                  >
                    Google Account permissions
                  </a>
                  .
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>



      {/* FINAL CTA — Cinematic */}

      <section className="cinematic-section">
        <div className="relative min-h-[60vh] overflow-hidden bg-gradient-to-br from-primary/20 via-background to-accent/10">
          {/* Ambient glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-full bg-primary/10 blur-[120px] animate-glow" />

          <div className="relative z-10 flex min-h-[60vh] items-center">
            <div className="container mx-auto px-6 text-center">
              <ScrollReveal>
                <h2 className="mx-auto max-w-3xl font-display text-4xl font-bold leading-tight text-foreground md:text-6xl">
                  Discover who you're
                  <br />
                  <span className="gradient-text">becoming</span>.
                </h2>
                <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
                  Personal, specific questions. 30–60 minutes. Written in plain language, with our working shown all the way through.
                </p>
                <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground">
                  Your future should be a choice, not a guess.
                </p>
                <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
                  <MagneticButton as="div" strength={0.3}>
                    <Button size="lg" className="h-14 rounded-full px-10 text-base font-semibold hover-glow" asChild>
                      <Link to="/payment">
                        Start My Assessment <ArrowRight className="ml-2 h-5 w-5" />
                      </Link>
                    </Button>
                  </MagneticButton>
                  <Button
                    variant="outline"
                    size="lg"
                    className="h-14 rounded-full border-foreground/20 px-10 text-base font-semibold hover:bg-foreground/5"
                    asChild
                  >
                    <a href="https://www.wekitmentoring.com/" target="_blank" rel="noopener noreferrer">
                      Explore WeKIT™ Mentoring
                    </a>
                  </Button>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      <Testimonials />

      <WCC360LeadCta />

      <GlobalLaunchWaitlist />


      <SharedFooter />
    </div>
  );
};

export default Index;
